import moment from "moment";

import type {
  RelatedPublicSegment,
  RelatedPublicStop,
  CityDetails,
  RelatedPublicIntermediateStop,
  PublicSegment,
  PublicStop,
  PublicIntermediateStop,
  RelatedPublicSegmentV4,
  PublicSegmentV4
} from "@busbud/core-types/dist/lib/poll-service/api";
import { VehicleType } from "@busbud/int-schemas/lib/vehicles";
import type { MappedOperator } from "@busbud/int-schemas/udm";

import type {
  NormalizedTripStopAndTransfers,
  TransferStop,
  IntermediateStop,
  DepartureStop,
  ArrivalStop
} from "../../components/itinerary/types";

import { removeTimeOffset } from "../time";

/**
 * Helpers to extract data from UDM models
 */

const getStopName = (stop: RelatedPublicStop | PublicStop | null) => {
  return stop?.name ?? "";
};

const getStopLatLon = (stop: RelatedPublicStop | PublicStop | null) => {
  return {
    lat: stop?.lat || null,
    lon: stop?.lon || null
  };
};

const getCityName = (city: CityDetails | null) => city?.name || "";

const getSegmentServiceDetails = (
  segment:
    | RelatedPublicSegment
    | PublicSegment
    | RelatedPublicSegmentV4
    | PublicSegmentV4
) => {
  const { vehicle } = segment;
  const is_train = vehicle.type === VehicleType.Train;

  return {
    service_name: is_train ? vehicle.service_name : "",
    service_number: is_train ? vehicle.service_number : ""
  };
};

/**
 * Stops generator functions
 */

export const generateDepartureStopFromRelatedModels = (
  segment: RelatedPublicSegment | RelatedPublicSegmentV4,
  trip_locations: Record<string, RelatedPublicStop>,
  trip_operators: Record<string, MappedOperator>,
  trip_cities: Record<string, CityDetails>
): DepartureStop => {
  const { origin_id, operator_id } = segment;
  const stop = trip_locations[origin_id];
  if (!stop) {
    throw new Error(`could not find the stop with id ${origin_id}`);
  }
  const city = (stop.city_id && trip_cities[stop.city_id]) || null;
  const operator = trip_operators[operator_id] || null;

  return _generateDepartureStop(segment, stop, city, operator);
};

export const generateDepartureStopFromPublicSegment = (
  segment: PublicSegment | PublicSegmentV4
): DepartureStop => {
  return _generateDepartureStop(
    segment,
    segment.origin,
    segment.origin.city,
    segment.operator
  );
};

const _generateDepartureStop = (
  segment:
    | RelatedPublicSegment
    | PublicSegment
    | RelatedPublicSegmentV4
    | PublicSegmentV4,
  stop: RelatedPublicStop | PublicStop,
  city: CityDetails | null,
  operator: MappedOperator | null
): DepartureStop => {
  const { vehicle } = segment;
  const { service_name, service_number } = getSegmentServiceDetails(segment);
  const departure_time_string = removeTimeOffset(
    segment.departure_time?.timestamp || ""
  );
  const arrival_time_string = removeTimeOffset(
    segment.arrival_time?.timestamp || ""
  );
  const should_show_duration = !segment.stops?.length;

  return {
    stop_location_type: stop.location_type,
    departure_city_name: city?.name || "",
    departure_lat: stop.lat || null,
    departure_lon: stop.lon || null,
    departure_name: stop.name,
    departure_time: departure_time_string,
    departure_vehicle: vehicle.type,
    duration: should_show_duration
      ? moment(arrival_time_string).diff(departure_time_string, "minutes")
      : 0,
    service_name,
    service_number,
    transfer: false,
    ...(!!operator && { operator })
  };
};

export const generateTransferStopFromRelatedModels = (
  previous_segment: RelatedPublicSegment | RelatedPublicSegmentV4,
  next_segment: RelatedPublicSegment | RelatedPublicSegmentV4,
  trip_locations: Record<string, RelatedPublicStop>,
  trip_operators: Record<string, MappedOperator>,
  trip_cities: Record<string, CityDetails>
): TransferStop => {
  const previous_segment_stop =
    trip_locations[previous_segment.destination_id] ?? null;
  // @ts-expect-error TS18047 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const previous_segment_city = previous_segment_stop.city_id
    ? // @ts-expect-error TS18047 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      trip_cities[previous_segment_stop.city_id]
    : null;
  const next_segment_stop = trip_locations[next_segment.origin_id] ?? null;
  // @ts-expect-error TS18047 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const next_segment_city = next_segment_stop.city_id
    ? // @ts-expect-error TS18047 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      trip_cities[next_segment_stop.city_id]
    : null;
  const operator = trip_operators[next_segment.operator_id] ?? null;

  return _generateTransferStop({
    previous_segment,
    next_segment,
    previous_segment_stop,
    next_segment_stop,
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    previous_segment_city,
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    next_segment_city,
    operator
  });
};

export const generateTransferStopFromPublicSegments = (
  previous_segment: PublicSegment | PublicSegmentV4,
  next_segment: PublicSegment | PublicSegmentV4
): TransferStop => {
  const next_segment_stop = next_segment.origin;
  const next_segment_city = next_segment_stop.city;
  const previous_segment_stop = previous_segment.destination;
  const previous_segment_city = previous_segment_stop.city;
  const operator = next_segment.operator;

  return _generateTransferStop({
    previous_segment,
    next_segment,
    next_segment_stop,
    next_segment_city,
    previous_segment_stop,
    previous_segment_city,
    operator
  });
};

interface GenerateTransferStopParams {
  previous_segment:
    | RelatedPublicSegment
    | PublicSegment
    | RelatedPublicSegmentV4
    | PublicSegmentV4;
  next_segment:
    | RelatedPublicSegment
    | PublicSegment
    | RelatedPublicSegmentV4
    | PublicSegmentV4;
  previous_segment_stop: RelatedPublicStop | PublicStop | null;
  next_segment_stop: RelatedPublicStop | PublicStop | null;
  previous_segment_city: CityDetails | null;
  next_segment_city: CityDetails | null;
  operator: MappedOperator | null;
}

const _generateTransferStop = (
  params: GenerateTransferStopParams
): TransferStop => {
  const {
    previous_segment,
    next_segment,
    next_segment_stop,
    previous_segment_stop,
    next_segment_city,
    previous_segment_city,
    operator
  } = params;

  const previous_segment_arrival_time_string = removeTimeOffset(
    previous_segment.arrival_time?.timestamp || ""
  );
  const next_segment_departure_time_string = removeTimeOffset(
    next_segment.departure_time?.timestamp || ""
  );
  const next_segment_arrival_time_string = removeTimeOffset(
    next_segment.arrival_time?.timestamp || ""
  );

  const transfer_duration =
    previous_segment_arrival_time_string && next_segment_departure_time_string
      ? moment(next_segment_departure_time_string).diff(
          previous_segment_arrival_time_string,
          "minutes"
        )
      : 0;

  const next_segment_duration =
    next_segment_departure_time_string && next_segment_arrival_time_string
      ? moment(next_segment_arrival_time_string).diff(
          next_segment_departure_time_string,
          "minutes"
        )
      : 0;

  const { service_name, service_number } =
    getSegmentServiceDetails(next_segment);

  return {
    arrival_name: getStopName(previous_segment_stop),
    arrival_time: previous_segment_arrival_time_string,
    arrival_lat: getStopLatLon(previous_segment_stop).lat,
    arrival_lon: getStopLatLon(previous_segment_stop).lon,
    arrival_city_name: getCityName(previous_segment_city),
    departure_city_name: getCityName(next_segment_city),
    departure_name: getStopName(next_segment_stop),
    departure_time: next_segment_departure_time_string,
    departure_lat: getStopLatLon(next_segment_stop).lat,
    departure_lon: getStopLatLon(next_segment_stop).lon,
    departure_vehicle: next_segment.vehicle.type,
    duration: next_segment_duration,
    transfer_duration,
    service_name,
    service_number,
    ...(operator && { operator }),
    transfer: true
  };
};

export const generateIntermediateStopFromRelatedModels = (
  segment: RelatedPublicSegment | RelatedPublicSegmentV4,
  stop: RelatedPublicIntermediateStop,
  trip_operators: Record<string, MappedOperator>,
  trip_cities: Record<string, CityDetails>
): IntermediateStop => {
  const operator = trip_operators[segment.operator_id] || null;
  const city = stop.city_id ? trip_cities[stop.city_id] : null;
  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return _generateIntermediateStop(stop, operator, city);
};

export const generateIntermediateStopFromPublicSegment = (
  segment: PublicSegment | PublicSegmentV4,
  stop: PublicIntermediateStop
): IntermediateStop => {
  return _generateIntermediateStop(stop, segment.operator, segment.origin.city);
};

const _generateIntermediateStop = (
  stop: RelatedPublicIntermediateStop | PublicIntermediateStop,
  operator: MappedOperator | null,
  city: CityDetails | null
): IntermediateStop => {
  /**
   * On Intermediate Stops, the departure and arrival times are inverted.
   * Departure time means the time the vehicle leaves the stop, and arrival time means the time the vehicle arrives at the stop.
   * REF: https://docs.busbud-int.com/api/#operation/getAllSegmentStops
   */
  const departure_time_string = removeTimeOffset(
    stop.departure_time?.timestamp || ""
  );
  const arrival_time_string = removeTimeOffset(
    stop.arrival_time?.timestamp || ""
  );
  const city_name = city?.name ?? "";

  return {
    arrival_name: stop.name,
    arrival_city_name: city_name,
    arrival_time: arrival_time_string,
    arrival_lat: stop.lat || null,
    arrival_lon: stop.lon || null,
    departure_name: stop.name,
    departure_time: departure_time_string,
    departure_lat: stop.lat || null,
    departure_lon: stop.lon || null,
    departure_city_name: city_name,
    duration: moment(departure_time_string).diff(
      arrival_time_string,
      "minutes"
    ),
    ...(operator && { operator }),
    service_name: null,
    service_number: null,
    transfer: false
  };
};

export const generateArrivalStopFromRelatedModels = (
  segment: RelatedPublicSegment | RelatedPublicSegmentV4,
  trip_locations: Record<string, RelatedPublicStop>,
  trip_operators: Record<string, MappedOperator>,
  trip_cities: Record<string, CityDetails>
): ArrivalStop => {
  const { destination_id, operator_id } = segment;
  const stop = trip_locations[destination_id];
  if (!stop) {
    throw new Error(`could not find the stop with id ${destination_id}`);
  }
  const city = (stop.city_id && trip_cities[stop.city_id]) || null;
  const operator = trip_operators[operator_id] || null;

  return _generateArrivalStop(segment, stop, city, operator);
};

export const generateArrivalStopFromPublicSegment = (
  segment: PublicSegment | PublicSegmentV4
): ArrivalStop => {
  return _generateArrivalStop(
    segment,
    segment.destination,
    segment.destination.city,
    segment.operator
  );
};

const _generateArrivalStop = (
  segment:
    | RelatedPublicSegment
    | PublicSegment
    | RelatedPublicSegmentV4
    | PublicSegmentV4,
  stop: RelatedPublicStop | PublicStop,
  city: CityDetails | null,
  operator: MappedOperator | null
): ArrivalStop => {
  const arrival_time_string = removeTimeOffset(
    segment.arrival_time?.timestamp || ""
  );
  const { service_name, service_number } = getSegmentServiceDetails(segment);

  return {
    stop_location_type: stop.location_type,
    arrival_name: stop.name,
    arrival_time: arrival_time_string,
    arrival_city_name: city?.name || "",
    arrival_lat: stop.lat || null,
    arrival_lon: stop.lon || null,
    duration: 0,
    service_name,
    service_number,
    transfer: false,
    ...(!!operator && { operator })
  };
};

const generateIntermediateStopListFromRelatedModels = (
  segment: RelatedPublicSegment | RelatedPublicSegmentV4,
  trip_operators: Record<string, MappedOperator>,
  trip_cities: Record<string, CityDetails>
): IntermediateStop[] => {
  const { stops } = segment;
  if (!stops) {
    return [];
  }

  return stops.map<IntermediateStop>(stop =>
    generateIntermediateStopFromRelatedModels(
      segment,
      stop,
      trip_operators,
      trip_cities
    )
  );
};

const generateIntermediateStopListFromPublicSegment = (
  segment: PublicSegment | PublicSegmentV4
): IntermediateStop[] => {
  const { stops } = segment;
  if (!stops) {
    return [];
  }

  return stops.map<IntermediateStop>(stop =>
    generateIntermediateStopFromPublicSegment(segment, stop)
  );
};

export const tripStopsGenerationFromRelatedModels = (
  trip_segments: (RelatedPublicSegment | RelatedPublicSegmentV4)[],
  trip_locations: Record<string, RelatedPublicStop>,
  trip_operators: Record<string, MappedOperator>,
  trip_cities: Record<string, CityDetails>
) => {
  return trip_segments.reduce<NormalizedTripStopAndTransfers>(
    (trip_stops, segment, idx) => {
      if (idx === 0) {
        trip_stops.push(
          generateDepartureStopFromRelatedModels(
            segment,
            trip_locations,
            trip_operators,
            trip_cities
          )
        );

        if (segment.stops && segment.stops.length) {
          trip_stops.push(
            generateIntermediateStopListFromRelatedModels(
              segment,
              trip_operators,
              trip_cities
            )
          );
        }
      }

      // If we have more than 1 segment, means we have a transfer
      if (idx > 0) {
        trip_stops.push(
          generateTransferStopFromRelatedModels(
            // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            trip_segments[idx - 1],
            segment,
            trip_locations,
            trip_operators,
            trip_cities
          )
        );

        if (segment.stops && segment.stops.length) {
          trip_stops.push(
            generateIntermediateStopListFromRelatedModels(
              segment,
              trip_operators,
              trip_cities
            )
          );
        }
      }

      // If is the last segment we add the arrival stop
      if (idx === trip_segments.length - 1) {
        trip_stops.push(
          generateArrivalStopFromRelatedModels(
            segment,
            trip_locations,
            trip_operators,
            trip_cities
          )
        );
      }

      return trip_stops;
    },
    []
  );
};

export const tripStopsGenerationFromPublicSegments = (
  segments: (PublicSegment | PublicSegmentV4)[]
) => {
  return segments.reduce<NormalizedTripStopAndTransfers>(
    (trip_stops, segment, idx) => {
      if (idx === 0) {
        trip_stops.push(generateDepartureStopFromPublicSegment(segment));

        if (segment.stops && segment.stops.length) {
          trip_stops.push(
            generateIntermediateStopListFromPublicSegment(segment)
          );
        }
      }

      // If we have more than 1 segment, means we have a transfer
      if (idx > 0) {
        trip_stops.push(
          // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          generateTransferStopFromPublicSegments(segments[idx - 1], segment)
        );

        if (segment.stops && segment.stops.length) {
          trip_stops.push(
            generateIntermediateStopListFromPublicSegment(segment)
          );
        }
      }

      // If is the last segment we add the arrival stop
      if (idx === segments.length - 1) {
        trip_stops.push(generateArrivalStopFromPublicSegment(segment));
      }

      return trip_stops;
    },
    []
  );
};
